/**
 * DISPLAY-ONLY census aggregation for the daily view's group headers and census tooltips.
 *
 * ============================ THE ONE RULE FOR THIS MODULE ============================
 * Nothing here may EVER be used as the census of a location, or fed into any staffing
 * calculation — not `requiredAmountForLocation`, not a ratio ladder, not a gap, not HPPD
 * for anything narrower than the group itself.
 *
 * The only legitimate census for a location is
 *   `resolveLocationCensus(location, byDimensions, totalCensus, statsMode, allowAvailabilityFallback)`
 * from `censusResolution.ts`, and its backend twin `util/census_resolution.py`.
 * ======================================================================================
 *
 * Why this is a hard prohibition and not a style preference: the numbers here are keyed by the
 * USER'S GROUPING PREFERENCE (`censusByLocation`), which is a per-user display setting. A unit-mode
 * header shows `unit=2N`; a both-mode header over the same locations shows `unit=2N|floor=F1`. Those
 * are different numbers for the same staff. Feeding either into staffing math makes required amounts,
 * gaps and the call-off gate depend on which dropdown a user picked — which is precisely the bug this
 * PR exists to remove, and which previously meant two head nurses in one department saw different
 * required amounts for the same shift.
 *
 * A group census is also a COARSER slice than any one member location's: it answers "how many patients
 * are on this unit", not "how many patients does this location cover". Using it per-location silently
 * reintroduces the cross-dimension borrowing the canonical resolver was written to stop.
 *
 * If you need a number for staffing, you are in the wrong file.
 */

import { ALLOW_AVAILABILITY_FALLBACK, censusDimensionKey, resolveLocationCensus, UNASSIGNED_DIMENSION_VALUE } from "./censusResolution";
import { type DepartmentLocation, LocationMode, type StatsMode } from "./models";

/**
 * One area a group's census is made of, as raw dimension values so the caller can translate the
 * label. `rooms` is sorted and lowercased, matching how the census keys are built.
 */
export type CensusDisplayGroupPart = { unit: string | null; floor: string | null; rooms: string[]; census: number };

/**
 * One row of a census breakdown: the display-group key and the census shown on that header.
 *
 * `census` is a HEADER number. See the module contract above — it is not a location's census and must
 * not reach any staffing calculation.
 *
 * `parts` are the areas the group covers, and `census` is the WIDEST of them (see `partsForAutoGroup`
 * for why not the sum). Floor / Unit / Both always have exactly one — the dimension the mode names.
 * Only Auto can have several, because its key is a NAME that may span differently-mapped locations;
 * that is the case worth showing a breakdown for.
 */
export type CensusDisplayGroup = { key: string; census: number; parts: CensusDisplayGroupPart[] };

/**
 * The census shown on each display-group header. DISPLAY ONLY — see the module contract above.
 *
 * The one source of truth for which buckets the daily view shows and what number sits on each, so
 * headers and tooltips cannot disagree.
 *
 * A group's number is the census of the DIMENSION it is keyed by (`unit=2N`), never of a member
 * location — a member may be mapped more finely and would put its sub-slice under a header claiming
 * the whole unit, varying with the arbitrary order `locations` arrives in. One key lookup, not a sum:
 * several templates can cover one area, and summing would double-count its patients.
 *
 * Auto groups by location NAME, which is not a dimension and so does not pin down an area: several
 * locations can share a name and map differently. Its number is therefore the WIDEST of the group's
 * distinct mappings — see `partsForAutoGroup`. None has no grouping and returns [].
 */
export const censusDisplayGroups = (
	mode: LocationMode,
	byDimensions: Record<string, number>,
	totalCensus: number,
	locations: DepartmentLocation[],
	statsMode: StatsMode,
): CensusDisplayGroup[] => {
	// DEFAULT: no groups at all in None mode — the user asked for an ungrouped table, so there are no
	// headers to price.
	if (mode === LocationMode.None) return [];

	// No dimensions AND no census is the "there is no census data for this day" case (no EMR feed, no
	// manual entry). An empty list makes the breakdown tooltip render nothing rather than a table of
	// zeros, which would read as "every bucket has no patients".
	//
	// `totalCensus` — not the emptiness of `byDimensions` — is what says whether census exists. A
	// department whose locations carry NO unit/floor/room mapping makes `build_census_grouping` emit
	// an empty map by construction (it only emits keys some location could be looked up by), so
	// gating on `byDimensions` alone zeroed exactly the departments the unmapped-location rule in
	// `resolveLocationCensus` exists to serve — it never got the chance to run.
	if (Object.keys(byDimensions).length === 0 && totalCensus <= 0) return [];

	// Collect every member of a group before pricing it. Taking the first member and moving on would
	// make Auto's number depend on the order `locations` arrives in — see `partsForAutoGroup`.
	const membersByKey = new Map<string, DepartmentLocation[]>();
	for (const location of locations) {
		const key = displayGroupKey(location, mode);
		if (key === undefined) continue;
		const members = membersByKey.get(key);
		if (members === undefined) membersByKey.set(key, [location]);
		else members.push(location);
	}

	const groups: CensusDisplayGroup[] = [];
	for (const [key, members] of membersByKey) {
		const parts = partsForDisplayGroup(members, mode, byDimensions, totalCensus, statsMode);
		groups.push({ key, census: Math.max(...parts.map((part) => part.census)), parts });
	}
	// Explicit locale: the default is the viewer's, so two users could otherwise see the same groups
	// in a different order.
	return groups.sort((a, b) => a.key.localeCompare(b.key, "en"));
};

/**
 * The census behind one display-group HEADER, over every location in that group.
 *
 * DISPLAY ONLY, and deliberately kept private to this module so it cannot become a second census
 * resolver. Exporting this, or passing its result to `requiredAmountForLocation`, breaks the
 * contract at the top of this file.
 *
 * Floor / Unit / Both read the exact dimension key the mode names, so the header shows the area it
 * claims to. Every member of such a group yields the SAME dimension key by construction — the key is
 * built from the very dimensions the group is keyed by — so any member can name it. A missing key is
 * 0: the same deliberate "no data for this slice" rule the resolver uses, written as an explicit
 * branch rather than a lookup default.
 */
const partsForDisplayGroup = (
	members: DepartmentLocation[],
	mode: LocationMode,
	byDimensions: Record<string, number>,
	totalCensus: number,
	statsMode: StatsMode,
): CensusDisplayGroupPart[] => {
	const dimensionKey = displayGroupDimensionKey(members[0], mode);
	if (dimensionKey === undefined) return partsForAutoGroup(members, byDimensions, totalCensus, statsMode);
	return [
		{
			unit: mode === LocationMode.Floor ? null : (emptyToUndefined(members[0].unit)?.trim() ?? null),
			floor: mode === LocationMode.Unit ? null : (emptyToUndefined(members[0].floor)?.trim() ?? null),
			rooms: [],
			census: dimensionKey in byDimensions ? byDimensions[dimensionKey] : 0,
		},
	];
};

/**
 * Auto's census, over the locations sharing one NAME — one part per distinct area.
 *
 * Auto is the only mode whose key does not determine a mapping: a name is a label, and several
 * locations can share one while mapping to different units/floors/rooms (48 such names across 29
 * production departments). So "the location's own census" has no single answer here, and reading it
 * off whichever member came first made the header depend on row order — the locations query has no
 * `ORDER BY`, so an edit that rewrites a row moves it and silently changes the number.
 *
 * The caller takes the WIDEST part, not their sum, because the areas may overlap and we only have
 * counts, never patient identities, so a true union is not computable. Newark's "Assignment 3" is the
 * worked example: one row maps to unit 3, another to unit 3 PLUS its 28 rooms. Both describe the same
 * 14 patients, so summing reports 28 for a unit that holds 14. The widest part can only ever
 * UNDER-state a group spanning genuinely disjoint areas — a bounded, honest error — whereas a sum can
 * exceed the department census, which is never a defensible number to show.
 */
const partsForAutoGroup = (
	members: DepartmentLocation[],
	byDimensions: Record<string, number>,
	totalCensus: number,
	statsMode: StatsMode,
): CensusDisplayGroupPart[] => {
	const parts = new Map<string, CensusDisplayGroupPart>();
	for (const member of members) {
		const mapping = mappingSignature(member);
		if (parts.has(mapping)) continue;
		parts.set(mapping, {
			unit: emptyToUndefined(member.unit)?.trim() ?? null,
			floor: emptyToUndefined(member.floor)?.trim() ?? null,
			rooms: (member.rooms ?? []).map((room) => room.room_name.trim().toLowerCase()).sort(),
			census: resolveLocationCensus(member, byDimensions, totalCensus, statsMode, ALLOW_AVAILABILITY_FALLBACK),
		});
	}
	return Array.from(parts.values());
};

/**
 * Identifies the AREA a location covers, so two rows over the same area are counted once.
 *
 * Normalised the way `censusDimensionKey` normalises — trimmed, rooms lowercased and sorted — because
 * two rows differing only in room order or letter case cover the identical area and must collapse.
 */
const mappingSignature = (location: DepartmentLocation): string =>
	JSON.stringify([
		emptyToUndefined(location.unit)?.trim() ?? null,
		emptyToUndefined(location.floor)?.trim() ?? null,
		(location.rooms ?? []).map((room) => room.room_name.trim().toLowerCase()).sort(),
	]);

/** The canonical dimension key a Floor/Unit/Both group header stands for; `undefined` for Auto. */
const displayGroupDimensionKey = (location: DepartmentLocation, mode: LocationMode): string | undefined => {
	switch (mode) {
		case LocationMode.Floor:
			return censusDimensionKey(null, emptyToUndefined(location.floor) ?? null, null);
		case LocationMode.Unit:
			return censusDimensionKey(emptyToUndefined(location.unit) ?? null, null, null);
		case LocationMode.Both:
			return censusDimensionKey(emptyToUndefined(location.unit) ?? null, emptyToUndefined(location.floor) ?? null, null);
		default:
			return undefined;
	}
};

/**
 * The bucket a location falls under for a grouping mode, or `undefined` when it has no value for
 * those dimensions and therefore no bucket of its own. Mirrors the row-group keys the staff table
 * builds (`floor`, `unit`, `unit|floor`, or the location name in Auto).
 */
export const displayGroupKey = (location: DepartmentLocation, mode: LocationMode): string | undefined => {
	switch (mode) {
		case LocationMode.Floor:
			return emptyToUndefined(location.floor);
		case LocationMode.Unit:
			return emptyToUndefined(location.unit);
		case LocationMode.Both: {
			const unit = emptyToUndefined(location.unit);
			const floor = emptyToUndefined(location.floor);
			return unit === undefined || floor === undefined ? undefined : `${unit}|${floor}`;
		}
		case LocationMode.Auto:
			return emptyToUndefined(location.location);
		default:
			return undefined;
	}
};

/**
 * The location an action started from a group header lands in: the first department location that falls under
 * that group for this mode, or `null` when none does (the Unassigned bucket, None mode's single "all" group).
 * `null` is deliberate — the caller leaves Location empty for the user to pick rather than guessing one.
 */
export const firstLocationInDisplayGroup = (locations: readonly DepartmentLocation[], mode: LocationMode, groupKey: string): string | null => {
	const match = locations.find((location) => displayGroupKey(location, mode) === groupKey);
	return match ? match.location : null;
};

/**
 * Where an add started from a group header should land for a person whose shift is already at `shiftLocation`:
 * that location itself when it belongs to the clicked group — a floor or unit group spans several locations,
 * and "moving" them to a sibling inside the group they are already in is not what the "+" asked for — else the
 * group's first location (`firstLocationInDisplayGroup`). A shift with no location (missing, `null` as the API
 * sends it, or empty) is in no group, so it gets the group's first.
 */
export const destinationInDisplayGroup = (
	locations: readonly DepartmentLocation[],
	mode: LocationMode,
	groupKey: string,
	shiftLocation: string | null | undefined,
): string | null => {
	if (shiftLocation && locations.some((location) => location.location === shiftLocation && displayGroupKey(location, mode) === groupKey)) {
		return shiftLocation;
	}
	return firstLocationInDisplayGroup(locations, mode, groupKey);
};

// Null as well as undefined: an unset unit/floor arrives from the backend as JSON `null`, which the
// DepartmentLocation type does not admit but the runtime value very much is.
const emptyToUndefined = (value: string | null | undefined): string | undefined => (value == null || value.trim() === "" ? undefined : value);

/**
 * Whether a display-group key is the backend's "the EMR reported nothing" token, which must be shown
 * as a translated label rather than rendered raw. Group keys come from location config so this is
 * rare, but the token must never reach a user.
 */
export const isUnassignedDisplayGroup = (key: string): boolean => key === UNASSIGNED_DIMENSION_VALUE;
